import { Table, TableBody, TableHead, TableRow } from '@mui/material';
import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';

import COverlayMessage from '../../components/COverlayMessage';
import { type IOverlayMessage } from '../../components/IOverlayMessage';
import { type ITableBaseCellContext } from '../../components/ITableBaseCellContext';
import { type TPandasDataFrameDict } from '../../components/TPandasDataFrameDict';
import { type TScaffoldTableColumnConfig } from '../../components/TScaffoldTableColumnConfig';
import { type TTableBaseRowContext } from '../../components/TTableBaseRowContext';
import { type ETableIds } from '../../enums/ETableIds';
import { updateStateAttribute } from '../../helpers/helper';
import { useAppSelector } from '../../hooks/hooks';
import CTableBaseCell, { type CTableBaseCellConfig } from './CTableBaseCell';

export interface ITableBaseProps {
  id: ETableIds;
  data: TPandasDataFrameDict | undefined;
  rowConfig: Partial<{
    header: Partial<{ style: (context: TTableBaseRowContext) => React.CSSProperties }>;
    body: Partial<{
      onClick: (context: TTableBaseRowContext) => void;
      style: (context: TTableBaseRowContext) => React.CSSProperties;
      link: (context: TTableBaseRowContext) => { to: string; target?: '_blank' };
    }>;
  }>;
  columnConfig: TScaffoldTableColumnConfig[];
  renderCoordinates?: boolean;
  onCellChange?: (change: Omit<ITableBaseCellContext, 'rowValues'>) => void;
  className?: string;
  edgeColumnConfig?: Partial<{
    prefix: TScaffoldTableColumnConfig;
    suffix: TScaffoldTableColumnConfig;
  }>;
  /**
   * Markdown message shown on top of the table, e.g. when its data is masked.
   */
  overlayMessage?: IOverlayMessage;
}

interface State {
  widthArray: number[];
}

export default function CTableBase(props: ITableBaseProps) {
  const ref = useRef<HTMLTableElement>(null);
  const [state, setState] = useState<State>({ widthArray: [] });
  const { dashboardOutletDimensions } = useAppSelector((state) => state.dashboard);

  const trueColumns = Object.keys(props.data ?? {});
  const rowNames = Array.from(new Set(trueColumns.flatMap((col) => Object.keys(props.data?.[col] ?? {}))).values());

  const defaultRowStyle = {
    backgroundColor: 'rgba(0, 0, 0, 0)'
  };

  const defaultCellStyle = (config?: Partial<CTableBaseCellConfig>) => {
    return {
      cursor: config?.onClick ? 'pointer' : 'inherit',
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
      paddingLeft: 0
    };
  };

  // Append index to table's first column
  const indicesDict: Record<string, string> = {};
  for (const ix of rowNames) {
    indicesDict[ix] = ix;
  }
  const augmentedData: TPandasDataFrameDict = { _prefix: indicesDict, ...props.data, _suffix: indicesDict };
  const augmentedColumnConfig: ITableBaseProps['columnConfig'] = [
    {
      span: props.edgeColumnConfig?.prefix ? 0.1 : 0,
      header: { render: () => <></> },
      body: { render: () => <></> },
      ...props.edgeColumnConfig?.prefix
    },
    ...props.columnConfig,
    {
      span: props.edgeColumnConfig?.suffix ? 0.1 : 0,
      header: { render: () => <></> },
      body: { render: () => <></> },
      ...props.edgeColumnConfig?.suffix
    }
  ];
  const prefixOffset = -1;

  // Build the column and rows data array
  const columns = Object.keys(augmentedData);
  const rows = rowNames.map((rowID) => columns.flatMap((col) => augmentedData[col][rowID] ?? null));

  // Compute the width ratios
  const widthRatios = columns?.map((_, ix) =>
    augmentedColumnConfig[ix]?.span === undefined ? 1 : augmentedColumnConfig[ix]?.span
  );
  const widthRatioSum = widthRatios?.length > 0 ? widthRatios?.reduce((a, b) => (a ?? 0) + (b ?? 0)) : 0;

  useEffect(() => {
    const recomputeWidthArray = () => {
      const widthArray = widthRatios?.map((e) =>
        Math.round(((e ?? 0) / (widthRatioSum ?? 0)) * (ref.current?.parentElement?.getBoundingClientRect().width ?? 0))
      );
      updateStateAttribute<State>({ widthArray }, setState);
    };

    recomputeWidthArray();
  }, [props, dashboardOutletDimensions.columnW]);

  return (
    <>
      {props.data && (
        <div className={'relative'}>
          <Table id={`table-${props.id}`} ref={ref} className={props.className}>
            <TableHead>
              <TableRow
                style={{
                  ...defaultRowStyle,
                  ...props.rowConfig?.header?.style?.({
                    rowName: 'header',
                    rowValues: columns,
                    cellCoordinate: [0, -1]
                  })
                }}
              >
                {columns.map((_, ix) => (
                  <CTableBaseCell
                    key={`table-${props.id}-col-${ix}`}
                    config={{
                      ...augmentedColumnConfig?.[ix]?.header,
                      style: (context) => {
                        return {
                          wordBreak: 'break-word',
                          ...defaultCellStyle(augmentedColumnConfig?.[ix]?.header),
                          ...augmentedColumnConfig?.[ix]?.header?.style?.(context)
                        };
                      }
                    }}
                    colWidth={state.widthArray[ix]}
                    fixWidth={augmentedColumnConfig?.[ix]?.fixWidth}
                    rowContext={columns.slice(1, columns.length - 1)}
                    cellCoordinate={[ix + prefixOffset, -1]}
                    rowName={'header'}
                    onChange={() => {}}
                    renderCoordinates={props.renderCoordinates}
                  />
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row, jx) => (
                <TableRow
                  key={`${props.id}-row-${jx}`}
                  onClick={() => {
                    props.rowConfig?.body?.onClick?.({
                      rowName: rowNames[jx],
                      rowValues: row,
                      cellCoordinate: [0, jx]
                    });
                  }}
                  style={{
                    cursor: props?.rowConfig?.body?.onClick ? 'pointer' : 'auto',
                    ...defaultRowStyle,
                    ...props.rowConfig?.body?.style?.({
                      rowName: rowNames[jx],
                      rowValues: row,
                      cellCoordinate: [0, jx]
                    })
                  }}
                >
                  {row.map((_, ix) => {
                    const config = {
                      ...augmentedColumnConfig?.[ix]?.body,
                      style: (context: any) => {
                        return {
                          ...defaultCellStyle(augmentedColumnConfig?.[ix]?.body),
                          ...(props.rowConfig?.body?.link?.(context)?.to
                            ? { margin: 0, paddingBottom: 0, paddingTop: 0 }
                            : augmentedColumnConfig?.[ix]?.body?.style?.(context))
                        };
                      }
                    };

                    if (props.rowConfig?.body?.link) {
                      config.render = (context: any) => (
                        <Link
                          className={'!h-full !cursor-pointer flex flex-col justify-center items-stretch w-full'}
                          to={props.rowConfig?.body?.link?.(context)?.to ?? ''}
                          target={props.rowConfig?.body?.link?.(context)?.target}
                          style={{
                            marginTop: '0',
                            marginBottom: '0',
                            ...defaultCellStyle(augmentedColumnConfig?.[ix]?.body),
                            ...augmentedColumnConfig?.[ix]?.body?.style?.(context)
                          }}
                        >
                          {augmentedColumnConfig?.[ix]?.body?.render?.(context) ?? (row[ix] ?? 'null').toString()}
                        </Link>
                      );
                    }

                    return (
                      <CTableBaseCell
                        key={`table-${props.id}-col-${ix}-row-${jx}`}
                        config={config}
                        colWidth={state.widthArray[ix]}
                        fixWidth={augmentedColumnConfig?.[ix]?.fixWidth}
                        rowContext={row.slice(1, row.length - 1)}
                        cellCoordinate={[ix + prefixOffset, jx]}
                        rowName={rowNames[jx]}
                        onChange={
                          props.onCellChange
                            ? (value) => {
                                props.onCellChange?.(value);
                              }
                            : () => {}
                        }
                        renderCoordinates={props.renderCoordinates}
                      />
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
          </Table>
          {props.overlayMessage && <COverlayMessage message={props.overlayMessage} />}
        </div>
      )}
    </>
  );
}
